import * as Sentry from '@sentry/react';
import { ElementKind } from '@shared/models/project.model';
import { OneOfChildFactoryElements } from '@shared/models/factory-element.model';
import { ElementCategoryID } from '@shared/models/element-categories.model';
import { getElementCategoryId } from '@shared/helpers/element-category-id.helpers';
import { assertUnreachable } from '@shared/helpers/function.helpers';
import {
  resolveElementPropertyValues,
  type InvalidElementPropertyValue,
  type SettableElementPropertyValues,
} from '@shared/helpers/element-property-values.helpers';

/**
 * Seed category property values onto an element factory so the new element is created with them
 * in a single write (DEV-3507). Only {@link ElementKind.Element} carries category properties;
 * other kinds ignore the request. Unsupported values are dropped and returned for reporting.
 */
export const applyPropertyValuesToFactory = <
  T extends OneOfChildFactoryElements,
>(
  defaults: T,
  propertyValues: SettableElementPropertyValues | undefined,
): { defaults: T; invalid: InvalidElementPropertyValue[] } => {
  if (!propertyValues) return { defaults, invalid: [] };

  switch (defaults.kind) {
    case ElementKind.Element:
      break;
    // Product elements carry no category properties, so there is nothing to seed.
    case ElementKind.Product:
      return { defaults, invalid: [] };
    default:
      return assertUnreachable(defaults);
  }

  const { properties, invalid } = resolveElementPropertyValues(
    getElementCategoryId(defaults),
    propertyValues,
  );

  if (!properties.length) return { defaults, invalid };

  const seededNames = new Set(properties.map((p) => p.name));
  return {
    defaults: {
      ...defaults,
      properties: [
        ...(defaults.properties ?? []).filter((p) => !seededNames.has(p.name)),
        ...properties,
      ],
    },
    invalid,
  };
};

/** Report dropped {@link SettableElementPropertyValues} without interrupting the add. */
export const reportInvalidPropertyValues = (
  invalid: InvalidElementPropertyValue[],
  categoryId: ElementCategoryID,
) => {
  if (!invalid.length) return;
  Sentry.withScope((scope) => {
    scope.setLevel('warning');
    scope.setExtras({ categoryId, invalid });
    Sentry.captureException(
      new Error('Ignored unsupported element property values on add'),
    );
  });
};
